import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, Search, Download, ChevronLeft, Clock, Phone, CheckCircle, XCircle, RefreshCw, MapPin, Mail, ArrowRight } from "lucide-react";
import axios from "axios";
import { SiteSelector, CommercialProvider, useSite } from "./SiteSelector";

const API = '/api';

const InterestsContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSite } = useSite();
  
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => { fetchInterests(); }, [page, statusFilter, selectedSite]);

  const fetchInterests = async () => {
    setLoading(true);
    try {
      let url = `${API}/kyle-rise/interests?page=${page}&limit=${limit}`;
      if (statusFilter !== "all") url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (selectedSite !== "all") url += `&site_id=${selectedSite}`;
      
      const res = await axios.get(url);
      setInterests(res.data.interests || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const updateStatus = async (interestId, newStatus, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/kyle-rise/interests/${interestId}/status?status=${newStatus}`);
      fetchInterests();
    } catch (err) { console.error(err); }
  };

  const exportCSV = () => {
    const headers = ["Interest ID", "Company", "Contact", "Email", "Phone", "Status", "Created"];
    const rows = interests.map(i => [i.interest_id, i.company_name, i.contact_name, i.email, i.phone, i.status, new Date(i.created_at).toLocaleString()]);
    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `interests-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "new": return { bg: "bg-blue-100", text: "text-blue-700", icon: Clock };
      case "contacted": return { bg: "bg-yellow-100", text: "text-yellow-700", icon: Phone };
      case "converted": return { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle };
      case "rejected": return { bg: "bg-red-100", text: "text-red-700", icon: XCircle };
      default: return { bg: "bg-gray-100", text: "text-gray-600", icon: Clock };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-interests">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/commercial")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-bold text-gray-900">Interest Pipeline</h1><p className="text-sm text-gray-500">{total} enquiries</p></div>
          </div>
          <div className="flex items-center gap-2">
            <SiteSelector />
            <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetchInterests()} placeholder="Search company, contact, email..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="converted">Converted</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Site</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (<tr><td colSpan={7} className="px-4 py-8 text-center"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-gray-400" /></td></tr>)
              : interests.length === 0 ? (<tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No enquiries found</td></tr>)
              : interests.map((interest) => {
                const statusStyle = getStatusBadge(interest.status);
                const StatusIcon = statusStyle.icon;
                return (
                <tr key={interest.interest_id} onClick={() => navigate(`/admin/commercial/interests/${interest.interest_id}`)} className="hover:bg-blue-50 cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><UserPlus className="w-5 h-5 text-indigo-600" /></div>
                      <div><p className="font-medium text-gray-900">{interest.company_name}</p><p className="text-xs text-gray-500">{interest.interest_id}</p></div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{interest.contact_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{interest.email}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <StatusIcon className={`w-4 h-4 ${statusStyle.text}`} />
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyle.bg} ${statusStyle.text}`}>{interest.status}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{interest.site_id ? <span className="flex items-center gap-1 text-xs text-gray-500"><MapPin className="w-3 h-3" /></span> : <span className="text-xs text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{new Date(interest.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      {interest.status === "new" && (<button onClick={(e) => updateStatus(interest.interest_id, "contacted", e)} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200">Mark Contacted</button>)}
                      {interest.status === "contacted" && (<button onClick={(e) => updateStatus(interest.interest_id, "converted", e)} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center gap-1">Convert <ArrowRight className="w-3 h-3" /></button>)}
                      <button onClick={() => window.location.href = `mailto:${interest.email}`} className="p-2 hover:bg-gray-100 rounded-lg" title="Email"><Mail className="w-4 h-4 text-blue-500" /></button>
                    </div>
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
        {pages > 1 && (<div className="flex items-center justify-between mt-4"><p className="text-sm text-gray-500">Page {page} of {pages}</p><div className="flex items-center gap-2"><button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Previous</button><button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="px-3 py-2 border rounded-lg text-sm disabled:opacity-50">Next</button></div></div>)}
      </div>
    </div>
  );
};

export default function CommercialInterests() {
  return (
    <CommercialProvider>
      <InterestsContent />
    </CommercialProvider>
  );
}
